/**
 * Hook to fetch Voice AI scheduling information
 *
 * Aggregates data from treatment request metadata and Voice AI runs
 * to provide next scheduled call time and last call timestamp.
 */

import { useMemo } from 'react';

import { useGetTaskRunsQuery } from '@/services/api/blandService';

interface UseVoiceAIScheduleResult {
  /** ISO timestamp of next scheduled Voice AI call, null if not scheduled */
  nextScheduled: string | null;

  /** ISO timestamp of most recent Voice AI call, null if no calls yet */
  lastCall: string | null;

  /** Whether Voice AI runs are currently loading */
  isLoading: boolean;

  /** Error from the Voice AI runs query */
  error?: unknown;
}

/**
 * Fetches Voice AI schedule information for a treatment request
 *
 * @param matterId - Matter ID for the request
 * @param taskId - Task ID linked to the treatment request (may be null)
 * @param nextScheduledCall - Next scheduled call timestamp from request metadata
 * @returns Schedule information with loading state and errors
 *
 * @example
 * const { nextScheduled, lastCall } = useVoiceAISchedule(
 *   matterId,
 *   request.task_id,
 *   request.metadata?.next_scheduled_call
 * );
 *
 * if (nextScheduled) {
 *   console.log(`Next call: ${formatDateTime(nextScheduled)}`);
 * }
 * if (lastCall) {
 *   console.log(`Last call: ${formatDateTime(lastCall)}`);
 * }
 */
export function useVoiceAISchedule(
  matterId: string,
  taskId: string | null | undefined,
  nextScheduledCall: string | null | undefined,
): UseVoiceAIScheduleResult {
  // Fetch Voice AI runs for this task - skip if no task ID
  const {
    data: runsResponse,
    isLoading,
    error,
  } = useGetTaskRunsQuery(
    {
      matterId,
      taskId: taskId!,
    },
    {
      skip: !taskId,
    },
  );

  // Find most recent call from runs
  const lastCall = useMemo((): string | null => {
    if (!runsResponse?.runs || runsResponse.runs.length === 0) {
      return null;
    }

    // Sort runs by created_at descending
    const sortedRuns = [...runsResponse.runs].sort(
      (a, b) =>
        new Date(b.created_at || 0).getTime() -
        new Date(a.created_at || 0).getTime(),
    );

    const mostRecentRun = sortedRuns[0];

    // Return created_at of most recent run
    return mostRecentRun?.created_at || null;
  }, [runsResponse]);

  return {
    nextScheduled: nextScheduledCall || null,
    lastCall,
    isLoading,
    error,
  };
}
